import type { IPayable } from '../@types/Payable.js';

type LocalItemId = IPayable['localId'];
type Task = () => Promise<void> | void;

type TaskManager = {
  add: (localItemId: LocalItemId, task: Task) => void;
  run: (localItemId: LocalItemId) => Promise<unknown[]>;
};

/**
 * This creates a task manager to add tasks linked to payable items that will
 * be executed when the payment request for an item is initiated.
 * This makes it possible for the pre-middlewares to add actions fo be
 * executed only if the user chooses to pay.
 *
 * @returns The object to add and run tasks
 */
export function createTasks(): TaskManager {
  const itemTasks: Record<LocalItemId, Task[]> = {};

  const add = (localItemId: LocalItemId, task: Task): void => {
    itemTasks[localItemId] = [...(itemTasks[localItemId] ?? []), task];
  };

  const run = (localItemId: LocalItemId): Promise<unknown[]> => {
    const tasks = itemTasks[localItemId] ?? [];

    return Promise.all(
      tasks.map((task) => {
        const result = task();
        return result instanceof Promise ? result : Promise.resolve(result);
      })
    );
  };

  return {
    add,
    run,
  } as const;
}
